import { useMemo, useState } from "react";

import clsx from "clsx";
import moment from "moment";

import { gql } from "@apollo/client";
import { useMmmAnnotations } from "@features/mmm/hooks/use-mmm-annotations";
import { useClient } from "@hooks/http/use-client";
import {
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList,
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_calendar_of_events,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import {
  pickMediaPlanOverlapAnnotation,
  VERIFY_CALENDAR_OF_EVENTS,
} from "@north-beam/nb-common";
import { useMMMClient } from "@pages/mmm/hooks/use-mmm-client";
import { IconButton, PrimaryButton } from "@shared/buttons";

import { useMediaPlanData } from "../../../hooks/use-media-plan";
import { useUpdateMediaPlanDraftStep } from "../../../hooks/use-update-media-plan-draft-step";
import { DEPRECATED_WIZARD_STEPS as WIZARD_STEPS } from "../../consts";
import { MediaPlanVerifyButton } from "../media-plan-verify-button";

import {
  type NewPromotionCalendar,
  PromotionCalendarModal,
} from "./promotion-calendar-modal";

interface Props {
  currentIndex: number;
  calendarOfEvents?: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_calendar_of_events[];
  selectedMediaPlan: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList;
}

export const PromotionCalendarStep = ({
  currentIndex,
  calendarOfEvents = [],
  selectedMediaPlan,
}: Props) => {
  const {
    verify,
    unverify,
    addPendingData,
    removePendingData,
    isUpdateMediaPlanDraftStepLoading,
  } = useUpdateMediaPlanDraftStep({
    draftStep: VERIFY_CALENDAR_OF_EVENTS,
    selectedMediaPlan,
  });

  const isDraft = selectedMediaPlan.state === "draft";
  const [mmmClient] = useMMMClient();

  const [showPendingDataModal, setShowPendingDataModal] = useState(false);
  const closeModal = () => {
    setShowPendingDataModal(false);
  };

  const isVerified =
    selectedMediaPlan.step_tracker[VERIFY_CALENDAR_OF_EVENTS].verified;
  const pendingData = useMemo(
    () =>
      selectedMediaPlan.step_tracker[VERIFY_CALENDAR_OF_EVENTS].pending_data,
    [selectedMediaPlan],
  );

  const _verify = async () => verify(calendarOfEvents);

  const { setMediaPlanList } = useMediaPlanData();

  const { createAnnotation, campaignTypes } = useMmmAnnotations({
    onCreateAnnotation(_cache, { data }) {
      const annotation = data?.mmmCreateCustomAnnotation;
      if (annotation) {
        setMediaPlanList((prevMediaPlanList) =>
          prevMediaPlanList.map((mediaPlan) => {
            const copyCalendarOfEvents = [...mediaPlan.calendar_of_events];
            if (pickMediaPlanOverlapAnnotation(annotation, mediaPlan)) {
              copyCalendarOfEvents.push(annotation);
            }
            return {
              ...mediaPlan,
              calendar_of_events: copyCalendarOfEvents,
            };
          }),
        );
      }
    },
  });

  const client = useClient();

  const _addPendingData = async ({
    description,
    annotatedDate,
    endDate,
    campaignType,
  }: NewPromotionCalendar) => {
    if (campaignTypes.includes(campaignType)) {
      const { data } = await createAnnotation(
        description,
        annotatedDate,
        endDate,
        campaignType,
      );
      if (data?.mmmCreateCustomAnnotation) {
        client.cache.modify({
          id: client.cache.identify({
            __typename: "mmmMediaPlan",
            id: selectedMediaPlan.id,
          }),
          fields: {
            calendar_of_events(existingCalendarOfEvents = []) {
              const newCalendarOfEventsRef = client.cache.writeFragment({
                data: data.mmmCreateCustomAnnotation,
                fragment: gql`
                  fragment AnnotationFields on Annotation {
                    id
                    description
                    annotatedDate
                    startDate
                    endDate
                    campaignType
                  }
                `,
              });

              if (newCalendarOfEventsRef) {
                return [
                  ...existingCalendarOfEvents,
                  { __ref: newCalendarOfEventsRef.__ref },
                ];
              }

              return existingCalendarOfEvents;
            },
          },
        });
      }
    } else {
      const newPendingData = JSON.stringify({
        description,
        annotatedDate,
        endDate,
        campaignType,
      });
      await addPendingData(newPendingData);
      setShowPendingDataModal(false);
      closeModal();
    }
  };

  return (
    <>
      <div>
        <div className="mb-4">
          <div className="text-lg font-semibold">
            {WIZARD_STEPS[currentIndex].title}
          </div>
          {WIZARD_STEPS[currentIndex].description && isDraft && (
            <div className="text-sm text-interstellar-1">
              {WIZARD_STEPS[currentIndex].description}
            </div>
          )}
        </div>
        <div
          className={clsx("overflow-y-auto", {
            "max-h-[360px]": pendingData.length === 0 && isDraft,
            "max-h-[420px]": pendingData.length === 0 && !isDraft,
            "max-h-[180px]": pendingData.length > 0,
          })}
        >
          {calendarOfEvents.length > 0 ? (
            calendarOfEvents.map((event) => (
              <div
                key={event.id}
                className="py-2 text-xs flex items-center justify-between gap-x-1.5 border-b border-b-[#E5E5E5]"
              >
                <div>{event.description}</div>
                <div>
                  {moment(event.startDate).format("MM/DD")} -{" "}
                  {moment(event.endDate).format("MM/DD")}
                </div>
              </div>
            ))
          ) : (
            // NOTE: COE NOT WORKING SHORT-TERM FIX
            <div></div>
            // <div className="text-xs text-interstellar-1">No events added</div>
          )}
        </div>
        {pendingData.length > 0 && (
          <>
            <div className="text-xs font-bold border-b border-b-[#E5E5E5] pt-4 pb-1">
              PENDING EVENTS
            </div>
            <div className="overflow-y-auto max-h-[160px]">
              {pendingData.map((pendingDatum, index) => (
                <div
                  key={pendingDatum}
                  className="py-2 text-xs flex items-center justify-between gap-x-1.5 border-b border-b-[#E5E5E5]"
                >
                  <PendingDatum pendingDatum={pendingDatum} />
                  <IconButton
                    variant="tertiary"
                    onClick={() => removePendingData(index)}
                    loading={isUpdateMediaPlanDraftStepLoading}
                  >
                    <i className="fa-regular fa-trash-can text-danger" />
                  </IconButton>
                </div>
              ))}
            </div>
          </>
        )}
        <MediaPlanVerifyButton
          className="mt-4"
          verify={_verify}
          unverify={unverify}
          isVerified={isVerified}
          isLoading={isUpdateMediaPlanDraftStepLoading}
        >
          {/* NOTE: COE NOT WORKING SHORT TERM FIX */}
          <PrimaryButton
            onClick={() =>
              window.open(
                mmmClient?.input_google_sheet_url,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            MANAGE EVENTS
          </PrimaryButton>
          {/* <IconButton
            variant="tertiary"
            className="px-2"
            leftIcon={<i className="fa-regular fa-plus" />}
            onClick={() => setShowPendingDataModal(true)}
          >
            ADD NEW EVENT
          </IconButton> */}
        </MediaPlanVerifyButton>
      </div>
      <PromotionCalendarModal
        isOpen={showPendingDataModal}
        closeModal={closeModal}
        isLoading={isUpdateMediaPlanDraftStepLoading}
        onSubmit={_addPendingData}
      />
    </>
  );
};

const PendingDatum = ({ pendingDatum }: { pendingDatum: string }) => {
  const pendingPromotionCalendar: NewPromotionCalendar | null = useMemo(() => {
    try {
      return JSON.parse(pendingDatum);
    } catch (error) {
      return null;
    }
  }, [pendingDatum]);

  if (!pendingPromotionCalendar) {
    return null;
  }

  return (
    <div className="flex items-center justify-between flex-grow">
      <div>{pendingPromotionCalendar.description}</div>
      <div>Campaign Type: {pendingPromotionCalendar.campaignType}</div>
      <div>
        {`${moment(pendingPromotionCalendar.annotatedDate).format("MM/DD")} -
          ${moment(pendingPromotionCalendar.endDate).format("MM/DD")}
        `}
      </div>
    </div>
  );
};
